import { FileNameTypes, FileTypes, FileUploadStatus } from '@/__generated__/graphql';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { Image } from '@/components/Analysis/shared/Image';
import { abyssColors } from '@/theme/colors';
import { theme } from '@/theme/theme';
import CloseIcon from '@mui/icons-material/Close';
import SmartDisplayIcon from '@mui/icons-material/SmartDisplay';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import { AttachmentCaption } from '../CaptionBox/AttachmentCaption';
import { AttachmentMenu } from './AttachmentMenu';
import { AttachmentCard, StyledRemoveButton } from './styles';
import { Attachment } from './type';
import { KPI_ATTACHMENTS_COLLECTION_NAME } from '@/modules/KpiTracker/constants';

type AttachmentDisplayProps = {
  collectionName: string;
  selectedAttachment: Attachment;
  selectedAttachmentIndex: number;
  showRemoveIcon: boolean;
  handleRemoveAttachment?: (index: number) => void;
  isEditing: boolean;
  setIsEditing: (editing: boolean) => void;
  attachmentCaption: string | undefined;
  setAttachmentCaption: (caption: string) => void;
  handleAttachmentCaption: () => void;
  handleCaption: (e: React.MouseEvent) => void;
  uploadProgress?: number;
  enableUpload?: boolean;
  cardHeight?: string;
  handleClick?: (e: React.MouseEvent, attachment?: Attachment) => void;
  isUploadDialog?: boolean;
  hasError?: boolean;
  handleError?: (success: boolean) => void;
  location?: string;
  handleSBSMode?: () => void;
  handleDownloadAttachment?: (index: number) => void;
};

export const AttachmentDisplay = ({
  collectionName,
  selectedAttachment,
  selectedAttachmentIndex,
  showRemoveIcon,
  handleRemoveAttachment,
  isEditing,
  setIsEditing,
  attachmentCaption,
  setAttachmentCaption,
  handleAttachmentCaption,
  handleCaption,
  uploadProgress = 0,
  cardHeight = '10rem',
  handleClick,
  isUploadDialog,
  hasError,
  handleError,
  location = '',
  handleSBSMode,
  handleDownloadAttachment,
}: AttachmentDisplayProps) => {
  const isImage = useMemo(() => {
    return (
      selectedAttachment.type.includes('image/') ||
      [FileTypes.Jpeg, FileTypes.Png].includes(selectedAttachment.type as FileTypes)
    );
  }, [selectedAttachment.type]);

  const isPdf = useMemo(() => {
    return (
      selectedAttachment.type.includes('application/pdf') ||
      selectedAttachment.type === FileTypes.Pdf
    );
  }, [selectedAttachment.type]);

  const isVideo = useMemo(() => {
    return (
      selectedAttachment.type.includes('video/mp4') || selectedAttachment.type === FileTypes.Mp4
    );
  }, [selectedAttachment.type]);

  const canViewSideBySide = useMemo(() => {
    return (
      selectedAttachment.fileNameType === FileNameTypes.ReferenceDrawing ||
      selectedAttachment.fileNameType === FileNameTypes.IsometricDrawing
    );
  }, [selectedAttachment.fileNameType]);

  const renderFileContent = () => {
    if (
      selectedAttachment.url &&
      selectedAttachment.isNew &&
      selectedAttachment.uploadStatus === FileUploadStatus.Inprogress
    ) {
      return (
        <Stack
          className={`${location}-attachment`}
          sx={{
            display: 'flex',
            height: cardHeight,
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: abyssColors.primary[100],
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress
              variant="determinate"
              value={uploadProgress}
              size={50}
              thickness={5}
              sx={{ color: abyssColors.secondary[500] }}
            />
            <Box
              sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="caption" component="div" color="text.secondary">
                {`${Math.round(uploadProgress)}%`}
              </Typography>
            </Box>
          </Box>
        </Stack>
      );
    } else if (isImage) {
      return (
        <Image
          src={`${selectedAttachment.base64}`}
          alt={selectedAttachment.name}
          imgSx={{ height: cardHeight, objectFit: 'cover' }}
          sx={{
            height: cardHeight,
            cursor: hasError ? 'not-allowed' : 'pointer',
            pointerEvents: hasError ? 'all' : 'auto',
            aspectRatio: '1 / 1',
          }}
          onLoadEnd={handleError}
          fallbackSize="2.2rem"
          hideErrorMessage
          className={`${location}-attachment ${location}-attachment-image`}
        />
      );
    } else if (isPdf || isVideo) {
      return (
        <a href={selectedAttachment.url} target="_blank" rel="noopener noreferrer">
          <Stack
            sx={{
              display: 'flex',
              height: cardHeight,
              justifyContent: 'center',
              alignItems: 'center',
              aspectRatio: '1 / 1',
            }}
            className={`${location}-attachment ${location}-attachment-${isPdf ? 'pdf' : 'video'}`}
          >
            {isPdf && <AbyssIcon iconName="pdf-file" size="2xl" />}
            {isVideo && (
              <SmartDisplayIcon sx={{ fontSize: 45, color: theme.palette.text.secondary }} />
            )}
          </Stack>
        </a>
      );
    }
  };

  return (
    <Stack alignItems="center" gap="0.4rem" width="fit-content">
      <Stack position="relative">
        <AttachmentCard
          sx={{ position: 'relative' }}
          onClick={(event) => {
            if (handleClick) handleClick(event, selectedAttachment);
          }}
          isPdf={isPdf}
          isVideo={isVideo}
          cardHeight={cardHeight}
        >
          {renderFileContent()}
          {!showRemoveIcon && collectionName !== KPI_ATTACHMENTS_COLLECTION_NAME && (
            <AttachmentMenu
              isPdf={isPdf}
              isVideo={isVideo}
              canViewSideBySide={canViewSideBySide}
              handleSBSMode={handleSBSMode}
              handleRemoveAttachment={handleRemoveAttachment}
              handleDownloadAttachment={handleDownloadAttachment}
              index={selectedAttachmentIndex}
              location={location}
            />
          )}
        </AttachmentCard>
        {showRemoveIcon && handleRemoveAttachment && (
          <StyledRemoveButton
            aria-label="delete"
            onClick={() => handleRemoveAttachment(selectedAttachmentIndex)}
          >
            <CloseIcon style={{ fontSize: '13px' }} />
          </StyledRemoveButton>
        )}
      </Stack>
      <AttachmentCaption
        isUploadDialog={isUploadDialog}
        collectionName={collectionName}
        isEditing={isEditing}
        setIsEditing={setIsEditing}
        attachmentCaption={attachmentCaption}
        setAttachmentCaption={setAttachmentCaption}
        handleAttachmentCaption={handleAttachmentCaption}
        handleCaption={handleCaption}
        selectedAttachment={selectedAttachment}
        location={location}
      />
    </Stack>
  );
};
